import { GetStaticProps, GetStaticPropsContext } from 'next';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import dynamic from 'next/dynamic';

import {
  apolloClient,
  defaultQueryResult,
  getPageQuery,
} from 'utils/apolloClient';
import { getCurrentLocale } from 'utils/getCurrentLocale';

import Hero from 'components/sections/Hero';
import { IJobSummary } from 'components/sections/JobOpenings/interfaces';
import { CAREERS_QUERY } from '../../graphql/queries';
import { prepareSeoFromPageData } from '../../components/core/SEO/interface';
import { CareersQueryType, CareersType } from '../../models/pageTypes/careers';
import { Page } from '../../models/pageTypes/page';
import { PageQuery } from '../../graphql/model/PageQuery';
import {
  JoinUsWrapper,
  Wrapper,
} from '../../styles/pages/styled/career/careers.styles';
import JobOpenings from '../../components/sections/JobOpenings';
import { TitleAs } from '../../models/enums/titleAs';

const DynamicJoinUs = dynamic(
  () => import('../../components/sections/JoinUsCareers')
);
const DynamicOurHrTeam = dynamic(
  () => import('../../components/sections/OurHrTeam')
);
const DynamicWhyQodeca = dynamic(
  () => import('../../components/sections/WhyQodeca')
);
const DynamicRecruitmentProcessCareers = dynamic(
  () => import('../../components/sections/RecruitmentProcessCareers')
);
const DynamicWelcomeToOurTeam = dynamic(
  () => import('../../components/sections/WelcomeToOurTeam')
);
const DynamicGetToKnow = dynamic(
  () => import('../../components/sections/GetToKnow')
);

const Careers = ({ data: { page } }: Page<CareersType>) => {
  return (
    <Wrapper>
      <Hero
        title={'Careers'}
        lottieProps={{
          id: 'career_hero_lottie',
          src: 'https://assets10.lottiefiles.com/packages/lf20_knwnvvne.json',
        }}
      />

      <JobOpenings
        noJobsOffers={RichText.asText(page.no_jobs_offers)}
        noJobsOffersEmail={RichText.asText(page.no_jobs_offers_email)}
        title={RichText.asText(page.openings_title)}
        description={page.description}
        items={page.job_openings}
        titleAs={TitleAs.h3}
        stickerActionLink={page.sticker_action_link!.url || ''}
        joinText={RichText.asText(page.join_text_secondary)}
        joinTextPrimary={RichText.asText(page.join_text)}
        playVideoText={RichText.asText(page.play_video_button_text)}
        sendCvText={RichText.asText(page.send_us_cv)}
        sendCvButton={RichText.asText(page.send_us_cv_button)}
        teamImage={page.team_image}
        teamImages={page.team_images}
        teamImagesMobile={page.team_images_mobile}
      />

      <DynamicWhyQodeca
        title={RichText.asText(page.benefits_title1)}
        benefits={page.benefits1}
        titleAs={TitleAs.h3}
      />

      <DynamicOurHrTeam
        title={RichText.asText(page.hr_team_title)}
        team={page.hr_team}
        titleAs={TitleAs.h4}
      />

      <DynamicGetToKnow
        title={RichText.asText(page.meet_us)}
        items={page.meet_us_stages}
        titleAs={TitleAs.h4}
      />

      <DynamicRecruitmentProcessCareers
        title={RichText.asText(page.recruitment_title)}
        items={page.recruitment_process}
        titleAs={TitleAs.h5}
      />

      <DynamicWelcomeToOurTeam
        titleAs={TitleAs.h5}
        title={RichText.asText(page.welcome_title)}
        description={page.welcome_description}
        images={page.welcome_images}
        mainImage={page.welcome_main_image}
      />

      <JoinUsWrapper>
        <DynamicJoinUs
          title={RichText.asText(page.join_us.title)}
          description={RichText.asText(page.join_us.description)}
          actionLabel={RichText.asText(page.join_us.action_label)}
          actionLink={page.join_us.action_link!.url || ''}
          titleAs={TitleAs.h6}
          joinUsText={RichText.asText(page.join_us_careers_text)}
          joinUsPhone={RichText.asText(page.join_us_careers_phone)}
          joinUsMail={RichText.asText(page.join_us_careers_email)}
          joinUsImage={page.join_us_careers_image}
        />
      </JoinUsWrapper>
    </Wrapper>
  );
};

export const getStaticProps: GetStaticProps = async (
  context: GetStaticPropsContext
) => {
  const currentLanguage: string = getCurrentLocale(context);

  const {
    data: { allCareers_pages, allCareer_pages, ...defaultData },
  } = await apolloClient.query<PageQuery<CareersQueryType>>(
    getPageQuery(
      {
        pageLang: currentLanguage,
        careerLang: currentLanguage,
      },
      'careers_query',
      CAREERS_QUERY
    )
  );

  return {
    props: {
      data: {
        ...defaultQueryResult(defaultData),
        seo: prepareSeoFromPageData(allCareers_pages.edges[0].node),
        page: {
          ...allCareers_pages.edges[0].node,
          job_openings: allCareer_pages.edges.map(
            (job: { node: IJobSummary }) => job.node
          ),
        },
      },
    },
  };
};

export default Careers;
